// Gems Mine — every sound, synthesised. No audio files (house rule: a decoded
// buffer is resident memory on phones that run hot, encoders pad loop points,
// and a file cannot follow the game).
//
// Carries the roulette audio scars so they are never re-earned here:
//   - ONE AudioContext, created lazily, auto-resume suppressed while hidden.
//   - The ambient track is a LOOKAHEAD SCHEDULER on the audio clock (main
//     thread jank cannot stutter it), with a RUN-SCOPED gain node + oscillator
//     register so stop/switch can never leave two beds playing.
//   - duck() OUTRANKS everything until it expires (_duckedUntil), and a
//     stopped run resets the duck (a stale gate once muted a fresh track).
//   - Backgrounded = suspended: an 8-second reveal tail has no business
//     playing to a pocket.

let _ctx: AudioContext | null = null;
let _on = true;
let _hidden = false;
const KEY = 'gm_sound';

function ctx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!_ctx) {
    const AC = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!AC) return null;
    try { _ctx = new AC(); } catch { return null; }   // audio degrades to silence, never to a stopped game
    document.addEventListener('visibilitychange', () => {
      _hidden = document.hidden;
      if (!_ctx) return;
      if (_hidden) { try { _ctx.suspend().catch(() => {}); } catch { /* gone */ } }
      else { try { _ctx.resume().catch(() => {}); } catch { /* gone */ } }
    });
  }
  if (_ctx && _ctx.state === 'suspended' && !_hidden) _ctx.resume().catch(() => {});
  return _ctx;
}

export function initGmSound() {
  try { const v = localStorage.getItem(KEY); if (v != null) _on = v === '1'; } catch { /* private */ }
  return _on;
}
export function isGmSoundOn() { return _on; }
export function setGmSoundOn(on: boolean) {
  _on = on;
  try { localStorage.setItem(KEY, on ? '1' : '0'); } catch { /* private */ }
  if (!on) stopGmMusic(); else startGmMusic();
  return _on;
}
export function unlockGmSound() { const c = ctx(); if (c && c.state === 'suspended' && !_hidden) c.resume().catch(() => {}); }

/* ─── primitives ─────────────────────────────────────────────────────────── */

function bus(gain: number) {
  const c = ctx();
  if (!c || !_on) return null;
  const g = c.createGain();
  g.gain.value = gain;
  g.connect(c.destination);
  return { c, g };
}

function tone(freq: number, dur: number, o: {
  type?: OscillatorType; to?: number; gain?: number; delay?: number;
} = {}) {
  const b = bus(o.gain ?? 0.08); if (!b) return;
  const t0 = b.c.currentTime + (o.delay ?? 0);
  const osc = b.c.createOscillator();
  osc.type = o.type ?? 'sine';
  osc.frequency.setValueAtTime(freq, t0);
  if (o.to) osc.frequency.exponentialRampToValueAtTime(o.to, t0 + dur);
  b.g.gain.setValueAtTime(0.0001, t0);
  b.g.gain.exponentialRampToValueAtTime(o.gain ?? 0.08, t0 + 0.008);
  b.g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(b.g); osc.start(t0); osc.stop(t0 + dur + 0.05);
}

function noise(dur: number, o: { f0?: number; f1?: number; q?: number; gain?: number; delay?: number } = {}) {
  const b = bus(o.gain ?? 0.06); if (!b) return;
  const t0 = b.c.currentTime + (o.delay ?? 0);
  const n = Math.floor(b.c.sampleRate * dur);
  const buf = b.c.createBuffer(1, n, b.c.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < n; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / n);
  const src = b.c.createBufferSource(); src.buffer = buf;
  const f = b.c.createBiquadFilter();
  f.type = 'bandpass';
  f.frequency.setValueAtTime(o.f0 ?? 1200, t0);
  if (o.f1) f.frequency.exponentialRampToValueAtTime(o.f1, t0 + dur);
  f.Q.value = o.q ?? 1;
  src.connect(f); f.connect(b.g); src.start(t0);
}

/* ─── the effects ────────────────────────────────────────────────────────── */

/** Pickaxe meets rock: a hard mid crack with a stone-dust tail. */
export const sStrike = () => {
  noise(0.09, { f0: 2600, f1: 900, q: 1.4, gain: 0.16 });
  noise(0.22, { f0: 700, f1: 300, q: 0.8, gain: 0.07, delay: 0.03 });
  tone(120, 0.08, { type: 'sine', to: 60, gain: 0.1 });
};

/** A gem comes free. Pitch rises with the RUNG — the climb is audible — and
 *  each kind adds its own colour note so twenty reveals never sound like one. */
const GEM_NOTE: Record<string, number> = { emerald: 659.25, ruby: 587.33, sapphire: 783.99, amber: 523.25, diamond: 987.77 };
export const sGem = (kind: string, rung: number) => {
  const base = GEM_NOTE[kind] ?? 659.25;
  const lift = Math.min(rung, 12) * 0.03;
  tone(base * (1 + lift), 0.5, { type: 'triangle', gain: 0.12 });
  tone(base * 1.5 * (1 + lift), 0.4, { type: 'sine', gain: 0.07, delay: 0.06 });
  noise(0.35, { f0: 6000, f1: 9500, q: 0.7, gain: 0.05, delay: 0.02 });   // the sparkle
  if (kind === 'diamond') tone(base * 2 * (1 + lift), 0.6, { type: 'sine', gain: 0.05, delay: 0.12 });
};

/** The tension tick as the CASH OUT amount grows a step. Felt, not heard. */
export const sRung = (rung: number) => {
  tone(880 + Math.min(rung, 14) * 60, 0.05, { type: 'square', gain: 0.035 });
};

/** The bomb. The bed ducks under this from the caller. */
export const sBoom = () => {
  tone(60, 0.7, { type: 'sine', to: 34, gain: 0.24 });
  noise(0.5, { f0: 900, f1: 120, q: 0.6, gain: 0.22 });
  noise(0.9, { f0: 300, f1: 70, q: 0.5, gain: 0.1, delay: 0.12 });
  tone(2400, 0.06, { type: 'square', gain: 0.05 });                        // the initial crack
};

/** Cashout — the platform's win language: rising arpeggio, octave answer,
 *  held crown, shimmer. Scales a touch with how far the climb went. */
export const sCashout = (rung: number) => {
  const k = 1 + Math.min(rung, 10) * 0.02;
  [523.25, 659.25, 783.99, 1046.5].forEach((f, i) =>
    tone(f * k, 0.32, { type: 'triangle', gain: 0.15, delay: i * 0.075 }));
  [1046.5, 1318.5, 1568].forEach((f, i) =>
    tone(f * k, 0.24, { type: 'triangle', gain: 0.1, delay: 0.32 + i * 0.055 }));
  tone(1568 * k, 0.55, { type: 'sine', gain: 0.06, delay: 0.48 });
  noise(0.6, { f0: 5000, f1: 9000, q: 0.7, gain: 0.045, delay: 0.36 });
};

/** The round begins: a rising two-note mine-horn, then a pickaxe clink —
 *  channel five of the six-channel start moment. */
export const sRoundStart = () => {
  tone(196, 0.22, { type: 'triangle', to: 262, gain: 0.09 });
  tone(262, 0.3, { type: 'triangle', to: 392, gain: 0.11, delay: 0.16 });
  tone(523.25, 0.1, { type: 'sine', gain: 0.05, delay: 0.2 });
  noise(0.05, { f0: 3200, f1: 2400, q: 3, gain: 0.06, delay: 0.45 });
  tone(1046.5, 0.09, { type: 'sine', to: 880, gain: 0.05, delay: 0.45 });
};

/** Streak stamp chime — escalates with the level (HOT / ON FIRE / LEGEND). */
export const sStreak = (level: number) => {
  const base = 659.25 * Math.pow(1.25, level);
  tone(base, 0.12, { type: 'triangle', gain: 0.08 });
  tone(base * 1.5, 0.16, { type: 'triangle', gain: 0.08, delay: 0.08 });
  if (level >= 2) tone(base * 2, 0.22, { type: 'triangle', gain: 0.09, delay: 0.16 });
  noise(0.1, { f0: 5200, f1: 7000, q: 2, gain: 0.03, delay: 0.05 });
};

export const sTap = () => { tone(880, 0.045, { type: 'sine', to: 620, gain: 0.06 }); };
export const sDenied = () => { tone(220, 0.1, { type: 'square', to: 180, gain: 0.05 }); tone(180, 0.14, { type: 'square', to: 150, gain: 0.05, delay: 0.09 }); };

/* ─── "DEEP SEAM" — the ambient bed ──────────────────────────────────────
   A cave: a low drone breathing under a slow minor-pentatonic music-box motif
   that never resolves (no leading tone — the loop-join doctrine from the
   roulette tracks: the top voice holds across the seam, so there is no seam).
   E minor pentatonic: E G A B D. Bar = 4s; 4-bar pass = 16s. */

const BAR = 4;
const MOTIF: [number, number, number][] = [
  // [bar, beat(0..3.75 step .25), freq]
  [0, 0, 659.25], [0, 1.5, 587.33], [0, 2.5, 493.88],
  [1, 0.5, 440], [1, 2, 493.88], [1, 3, 587.33],
  [2, 0, 659.25], [2, 1.5, 783.99], [2, 3, 659.25],
  [3, 1, 587.33], [3, 2.5, 659.25],   // ends on E — the same E bar 0 opens on
];
const DRONE = [82.41, 123.47];        // E2 + B2, a bare fifth — a cave, not a chord

let _mOn = true;
let _mPlaying = false;
let _mGain: GainNode | null = null;
let _mLive: { o: OscillatorNode; end: number }[] = [];
let _mT0 = 0; let _mNextBar = 0; let _mTick = 0;
let _duckedUntil = 0;
const MKEY = 'gm_music';
const M_MASTER = 0.4;

export function initGmMusic() {
  try { const v = localStorage.getItem(MKEY); if (v != null) _mOn = v === '1'; } catch { /* private */ }
  return _mOn;
}
export function isGmMusicOn() { return _mOn; }
export function setGmMusicOn(on: boolean) {
  _mOn = on;
  try { localStorage.setItem(MKEY, on ? '1' : '0'); } catch { /* private */ }
  if (on) startGmMusic(); else stopGmMusic();
  return _mOn;
}

const mLive = (o: OscillatorNode, end: number) => { _mLive.push({ o, end }); };

function scheduleBar(c: AudioContext, out: GainNode, index: number) {
  const at = _mT0 + index * BAR;
  // the drone: two sines, re-voiced per bar with long overlapping swells
  for (const f of DRONE) {
    const o = c.createOscillator(); const g = c.createGain();
    o.type = 'sine'; o.frequency.value = f;
    o.detune.value = f === DRONE[0] ? -4 : 4;
    g.gain.setValueAtTime(0.0001, at);
    g.gain.exponentialRampToValueAtTime(0.05, at + 1.4);
    g.gain.exponentialRampToValueAtTime(0.0001, at + BAR * 1.15);
    o.connect(g); g.connect(out);
    o.start(at); o.stop(at + BAR * 1.2); mLive(o, at + BAR * 1.2);
  }
  // the music box: short bright plucks, well above the drone
  const bar = index % 4;
  for (const [b, beat, f] of MOTIF) {
    if (b !== bar) continue;
    const t = at + beat;
    const o = c.createOscillator(); const g = c.createGain(); const lp = c.createBiquadFilter();
    o.type = 'triangle'; o.frequency.value = f;
    lp.type = 'lowpass'; lp.frequency.value = 2600; lp.Q.value = 0.7;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.055, t + 0.006);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 1.3);
    o.connect(lp); lp.connect(g); g.connect(out);
    o.start(t); o.stop(t + 1.35); mLive(o, t + 1.35);
  }
  // a distant drip, once a bar, never on the beat — the cave breathing
  const drip = at + 2.6 + (index % 3) * 0.3;
  const o = c.createOscillator(); const g = c.createGain();
  o.type = 'sine'; o.frequency.setValueAtTime(1800, drip);
  o.frequency.exponentialRampToValueAtTime(900, drip + 0.09);
  g.gain.setValueAtTime(0.0001, drip);
  g.gain.exponentialRampToValueAtTime(0.03, drip + 0.005);
  g.gain.exponentialRampToValueAtTime(0.0001, drip + 0.4);
  o.connect(g); g.connect(out);
  o.start(drip); o.stop(drip + 0.45); mLive(o, drip + 0.45);
}

function pump() {
  const c = ctx();
  if (!c || !_mPlaying || !_mGain) return;
  const horizon = c.currentTime + 4;
  const earliest = Math.floor((c.currentTime - _mT0) / BAR);
  if (_mNextBar < earliest) _mNextBar = earliest;
  while (_mT0 + _mNextBar * BAR < horizon) { scheduleBar(c, _mGain, _mNextBar); _mNextBar++; }
  if (_mLive.length > 96) _mLive = _mLive.filter((n) => n.end > c.currentTime);
}

export function startGmMusic() {
  if (!_mOn || !_on || _mPlaying) return;
  const c = ctx(); if (!c) return;
  _mGain = c.createGain(); _mGain.gain.value = 0; _mGain.connect(c.destination);
  _mPlaying = true;
  _mT0 = c.currentTime + 0.15; _mNextBar = 0;
  _mGain.gain.setValueAtTime(0.0001, c.currentTime);
  _mGain.gain.exponentialRampToValueAtTime(M_MASTER, c.currentTime + BAR);
  pump();
  _mTick = window.setInterval(pump, 500);
}

export function stopGmMusic() {
  window.clearInterval(_mTick); _mTick = 0;
  _mPlaying = false;
  _duckedUntil = 0;                       // the duck belongs to the run that scheduled it
  const c = ctx(); const g = _mGain; const mine = _mLive;
  _mGain = null; _mLive = [];
  if (!c || !g) return;
  const now = c.currentTime;
  g.gain.cancelScheduledValues(now);
  g.gain.setValueAtTime(Math.max(g.gain.value, 0.0001), now);
  g.gain.exponentialRampToValueAtTime(0.0001, now + 0.15);
  for (const n of mine) { try { n.o.stop(now + 0.18); } catch { /* done */ } }
  window.setTimeout(() => { try { g.disconnect(); } catch { /* gone */ } }, 420);
}

/** Pull the bed down under an event (boom, cashout). Outranks everything
 *  until it expires. */
export function duckGmMusic(ms = 1200) {
  const c = ctx(); if (!c || !_mGain || !_mPlaying) return;
  const now = c.currentTime;
  _mGain.gain.cancelScheduledValues(now);
  _mGain.gain.setValueAtTime(Math.max(_mGain.gain.value, 0.0001), now);
  _mGain.gain.exponentialRampToValueAtTime(M_MASTER * 0.18, now + 0.12);
  _mGain.gain.setValueAtTime(M_MASTER * 0.18, now + ms / 1000);
  _mGain.gain.exponentialRampToValueAtTime(M_MASTER, now + ms / 1000 + 0.6);
  _duckedUntil = now + ms / 1000 + 0.6;
}

export function bindGmMusicVisibility(): () => void {
  const onVis = () => { if (document.hidden) stopGmMusic(); else if (_mOn && _on) startGmMusic(); };
  document.addEventListener('visibilitychange', onVis);
  return () => document.removeEventListener('visibilitychange', onVis);
}
